import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, errMsg } from "@/lib/api";
import { PRICE_MODE_LABEL, RESOURCE_LABEL } from "@/lib/format";
import { Btn, Card, Field, ListEditor, PageTitle } from "@/components/admin/AdminUI";

const EXP_FIELDS = [
  { key: "name", label: "Nombre" }, { key: "image", label: "Foto", type: "media" },
  { key: "short", label: "Descripción corta", wide: true }, { key: "description", label: "Descripción", type: "textarea", wide: true },
  { key: "hora_inicio", label: "Hora inicio / check-in" }, { key: "hora_fin", label: "Hora término / check-out" },
  { key: "price_mode", label: "Modalidad de precio", type: "select", options: Object.entries(PRICE_MODE_LABEL) },
  { key: "min_people", label: "Mínimo a cobrar (personas)", type: "number" }, { key: "max_people", label: "Máximo de personas", type: "number" },
  { key: "base_guests", label: "Huéspedes base (cabaña)", type: "number" }, { key: "max_units", label: "Máx. espacios por reserva", type: "number" },
  { key: "active", label: "Visible y reservable", type: "switch" },
];
const PRICES = [["weekday", "Lunes a jueves (temp. baja)"], ["weekend", "Fin de semana (temp. baja)"], ["high_weekday", "Lunes a jueves (temp. alta)"], ["high_weekend", "Fin de semana (temp. alta)"]];

function ExpEditor({ e0 }) {
  const [e, setE] = useState({ ...e0, includes_txt: (e0.includes || []).join(", ") });
  const set = (k, v) => setE((p) => ({ ...p, [k]: v }));
  const save = async () => {
    try {
      await api.put(`/admin/experiences/${e.slug}`, { ...e, includes: e.includes_txt.split(",").map((s) => s.trim()).filter(Boolean) });
      toast.success(`${e.name} guardada`);
    } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <Card data-testid={`pricing-exp-${e.slug}`}>
      <h3 className="font-serif text-2xl mb-4">{e0.name}</h3>
      <div className="grid sm:grid-cols-2 gap-3">{EXP_FIELDS.map((f) => <Field key={f.key} f={f} value={e[f.key]} onChange={(v) => set(f.key, v)} testid={`exp-${e.slug}-${f.key}`} />)}</div>
      <p className="mt-5 font-semibold text-sm">Precios (CLP){e.slug === "cabana" ? " por noche" : ""}</p>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-2">
        {PRICES.map(([k, l]) => <Field key={k} f={{ label: l, type: "number" }} value={e.prices?.[k]} onChange={(v) => set("prices", { ...e.prices, [k]: v })} testid={`exp-${e.slug}-price-${k}`} />)}
      </div>
      <p className="mt-2 text-xs text-muted-foreground">Fin de semana: viernes y sábado (noches) o sábado y domingo (por el día). Si el precio de temporada alta es 0 se usa el de temporada baja.</p>
      <div className="grid grid-cols-2 gap-3 mt-4">
        <Field f={{ label: "Tipo de abono", type: "select", options: [["fixed", "Monto fijo"], ["percent", "Porcentaje del total"]] }} value={e.deposit?.type} onChange={(v) => set("deposit", { ...e.deposit, type: v })} testid={`exp-${e.slug}-deposit-type`} />
        <Field f={{ label: e.deposit?.type === "percent" ? "Abono (%)" : "Abono ($)", type: "number" }} value={e.deposit?.value} onChange={(v) => set("deposit", { ...e.deposit, value: v })} testid={`exp-${e.slug}-deposit-value`} />
      </div>
      <div className="mt-3"><Field f={{ label: "Incluye (separado por comas)", wide: true }} value={e.includes_txt} onChange={(v) => set("includes_txt", v)} testid={`exp-${e.slug}-includes`} /></div>
      <Btn className="mt-5" onClick={save} data-testid={`save-exp-${e.slug}`}>Guardar</Btn>
    </Card>
  );
}

export default function AdminPricing() {
  const [exps, setExps] = useState([]);
  const [s, setS] = useState(null);
  useEffect(() => {
    api.get("/admin/experiences").then((r) => setExps(r.data));
    api.get("/admin/settings").then((r) => setS(r.data));
  }, []);
  const saveSettings = async (keys) => {
    try { const r = await api.put("/admin/settings", Object.fromEntries(keys.map((k) => [k, s[k]]))); setS(r.data); toast.success("Guardado"); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!s) return null;
  const expOpts = exps.map((e) => [e.slug, e.name]);
  return (
    <div data-testid="admin-pricing-page">
      <PageTitle title="Precios, abonos y cupos" />
      <Tabs defaultValue="exp">
        <TabsList className="mb-6 flex-wrap h-auto">
          <TabsTrigger value="exp" data-testid="tab-experiences">Experiencias</TabsTrigger>
          <TabsTrigger value="seasons" data-testid="tab-seasons">Temporadas</TabsTrigger>
          <TabsTrigger value="caps" data-testid="tab-capacities">Cupos</TabsTrigger>
          <TabsTrigger value="promos" data-testid="tab-promotions">Promociones</TabsTrigger>
        </TabsList>
        <TabsContent value="exp" className="grid xl:grid-cols-2 gap-6">{exps.map((e) => <ExpEditor key={e.slug} e0={e} />)}</TabsContent>
        <TabsContent value="seasons">
          <Card>
            <p className="text-sm text-muted-foreground mb-4">Las fechas dentro de una temporada alta usan los precios de temporada alta.</p>
            <ListEditor testid="seasons-editor" items={s.seasons} onChange={(v) => setS({ ...s, seasons: v })} addLabel="Agregar temporada alta"
              newItem={{ name: "Temporada alta", start: "", end: "" }}
              fields={[{ key: "name", label: "Nombre", wide: true }, { key: "start", label: "Desde", type: "date" }, { key: "end", label: "Hasta", type: "date" }]} />
            <Btn className="mt-5" onClick={() => saveSettings(["seasons"])} data-testid="save-seasons-btn">Guardar temporadas</Btn>
          </Card>
        </TabsContent>
        <TabsContent value="caps">
          <Card>
            <p className="text-sm text-muted-foreground mb-4">Cupos diarios por defecto. Puedes cambiar el cupo de un día puntual desde el Calendario.</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {Object.keys(s.capacities).map((k) => <Field key={k} f={{ label: RESOURCE_LABEL[k] + (k === "dia_compartido" ? " (personas)" : ""), type: "number" }} value={s.capacities[k]}
                onChange={(v) => setS({ ...s, capacities: { ...s.capacities, [k]: v } })} testid={`capacity-${k}`} />)}
            </div>
            <Btn className="mt-5" onClick={() => saveSettings(["capacities"])} data-testid="save-capacities-btn">Guardar cupos</Btn>
          </Card>
        </TabsContent>
        <TabsContent value="promos">
          <Card>
            <ListEditor testid="promotions-editor" items={s.promotions} onChange={(v) => setS({ ...s, promotions: v })} addLabel="Nueva promoción"
              newItem={{ title: "", description: "", discount_percent: 0, experiences: [], start: "", end: "", active: true }}
              fields={[{ key: "title", label: "Título" }, { key: "discount_percent", label: "Descuento (%)", type: "number" },
                { key: "description", label: "Descripción", type: "textarea", wide: true }, { key: "start", label: "Desde", type: "date" },
                { key: "end", label: "Hasta", type: "date" }, { key: "experiences", label: "Aplica a", type: "multi", options: expOpts, wide: true },
                { key: "active", label: "Activa", type: "switch" }]} />
            <p className="mt-3 text-xs text-muted-foreground">El descuento se aplica automáticamente al precio de la experiencia (no a los extras) cuando la fecha de inicio está dentro del rango.</p>
            <Btn className="mt-5" onClick={() => saveSettings(["promotions"])} data-testid="save-promotions-btn">Guardar promociones</Btn>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
